// Wave 14 — CdA-vs-duration curve.
//
// The aero analog of the cycling power-duration curve. For each
// supported duration (30 s, 1 m, 2 m, 5 m, 10 m, 20 m, 1 h), the lowest
// sliding-window mean CdA the rider has held over any contiguous window
// of that length, across all sessions. Server-side computed and shipped
// via GET /sessions/me/dashboard.
//
// Renders one thin line per position class plus a thicker brand-green
// "best overall" line on top. Y axis inverted so "lower is better"
// reads upward. X axis is duration in seconds (linear with formatted
// labels — log would be cleaner for the wide range, but @mui/x-charts'
// log scale is currently fiddly with the few discrete data points we
// have; the linear axis with formatted ticks is plenty readable).

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { colors } from '../theme';
import { positionColor } from '../services/positionColors';

export interface CdaDurationPoint {
  durationSec: number;
  bestCda: number | null;
  byPosition: Record<string, number>;
}

interface Props {
  curve: CdaDurationPoint[];
  height?: number;
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  return `${Math.round(sec / 3600)}h`;
}

export default function CdaDurationCurve({ curve, height = 320 }: Props) {
  // Filter to durations where at least the overall has data.
  const active = useMemo(
    () => curve.filter((p) => p.bestCda != null),
    [curve],
  );

  // All position classes that show up in any duration's byPosition map.
  const positions = useMemo(() => {
    const set = new Set<string>();
    for (const p of curve) {
      for (const k of Object.keys(p.byPosition ?? {})) set.add(k);
    }
    return Array.from(set);
  }, [curve]);

  if (active.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: '0.875rem', color: colors.textMuted }}>
          Run a longer session — your CdA × duration curve appears once we have
          enough samples to compute one.
        </Typography>
      </Box>
    );
  }

  const x = active.map((p) => p.durationSec);

  // Per-position series first, drawn thin. Overall series last so it
  // renders on top in the brand color.
  const series = [
    ...positions.map((pos) => ({
      id: `pos-${pos}`,
      label: pos,
      color: positionColor(pos),
      data: active.map((p) =>
        p.byPosition[pos] != null ? p.byPosition[pos] : null,
      ),
      showMark: true,
      curve: 'monotoneX' as const,
      connectNulls: true,
    })),
    {
      id: 'overall',
      label: 'Best overall',
      color: colors.brand,
      data: active.map((p) => p.bestCda),
      showMark: true,
      curve: 'monotoneX' as const,
    },
  ];

  return (
    <Box sx={{ width: '100%', height }}>
      <LineChart
        height={height}
        xAxis={[
          {
            data: x,
            label: 'Duration',
            valueFormatter: (v: number) => formatDuration(v),
            scaleType: 'point',
          },
        ]}
        yAxis={[
          {
            label: 'CdA (m²)',
            reverse: true,
          },
        ]}
        series={series.map((s) => ({
          ...s,
          type: 'line' as const,
        }))}
      />
    </Box>
  );
}
